import cuid from "cuid";
import { get, isEmpty, isNil, keyBy, sortBy } from "lodash";
import { EDGE_KINDS, NODE_TYPES, PROCESS_TRIGGER_NAMES } from "utils/constants";
import {
  CONDITION_OP_VALUES,
  DEFAULT_CONDITION_OPERATION,
  fieldOperandOf,
  normalizeOperation,
  operationByValue,
} from "./catalog";
import { edgeSummary, isRoutingEdge } from "./graph";
import { isWaitingType } from "./step-tabs";

export const RULE_MODES = {
  CONDITIONAL: "CONDITIONAL",
  OTHERWISE: "OTHERWISE",
  ALWAYS: "ALWAYS",
};

export const EXIT_TYPES = {
  BACK: "BACK",
  EVENT: "EVENT",
  TIMER: "TIMER",
};

export const DELAY_BLOCKERS = {
  INPUT: "INPUT",
  WAIT: "WAIT",
  CATCH_ALL: "CATCH_ALL",
};

export const DELAY_UNITS = {
  DAYS: "DAYS",
  HOURS: "HOURS",
  MINUTES: "MINUTES",
  SECONDS: "SECONDS",
};

const SECONDS_BY_UNIT = {
  [DELAY_UNITS.DAYS]: 86400,
  [DELAY_UNITS.HOURS]: 3600,
  [DELAY_UNITS.MINUTES]: 60,
  [DELAY_UNITS.SECONDS]: 1,
};

const DEFAULT_TIMER_SECONDS = SECONDS_BY_UNIT[DELAY_UNITS.DAYS];

const INPUT_NODE_TYPES = [NODE_TYPES.STATUS, NODE_TYPES.FORM_STEP];

const edgeKind = (edge) => get(edge, "data.kind") || EDGE_KINDS.CONDITION;

const edgeOrder = (edge) => Number(get(edge, "data.order")) || 0;

const withLabel = (edge) => ({ ...edge, label: edgeSummary(edge.data) });

export const isBackEdge = (edge) =>
  edgeKind(edge) === EDGE_KINDS.EVENT &&
  get(edge, "data.event") === PROCESS_TRIGGER_NAMES.BACK;

export const rulesOf = (edges = [], source) =>
  sortBy(
    edges.filter((edge) => edge.source === source && isRoutingEdge(edge)),
    edgeOrder,
  );

export const exitsOf = (edges = [], source) =>
  edges.filter((edge) => edge.source === source && !isRoutingEdge(edge));

export const exitType = (edge) => {
  if (isBackEdge(edge)) return EXIT_TYPES.BACK;
  if (edgeKind(edge) === EDGE_KINDS.TIMER) return EXIT_TYPES.TIMER;
  return EXIT_TYPES.EVENT;
};

const conditionsOf = (edge) => get(edge, "data.conditions") || [];

export const isIncompleteCondition = (condition) =>
  isEmpty(get(condition, "leftOperand.accessor")) ||
  fieldOperandOf(condition) === "";

export const ruleMode = (edge, isLast) => {
  if (edgeKind(edge) === EDGE_KINDS.ALWAYS) return RULE_MODES.ALWAYS;
  if (!isEmpty(conditionsOf(edge))) return RULE_MODES.CONDITIONAL;
  return isLast ? RULE_MODES.OTHERWISE : RULE_MODES.ALWAYS;
};

export const describeRules = (edges = [], source) => {
  const rules = rulesOf(edges, source);
  let catchAllRank = null;
  return rules.map((edge, index) => {
    const mode = ruleMode(edge, index === rules.length - 1);
    const shadowedBy = catchAllRank;
    if (isNil(catchAllRank) && mode !== RULE_MODES.CONDITIONAL)
      catchAllRank = index + 1;
    return {
      edge,
      rank: index + 1,
      mode,
      shadowedBy,
      incomplete: conditionsOf(edge).some(isIncompleteCondition),
    };
  });
};

export const conditionParts = (condition, variables = [], labels = {}) => {
  const accessor = get(condition, "leftOperand.accessor");
  const variable = keyBy(variables, "accessor")[accessor];
  const operation =
    normalizeOperation(get(condition, "operation")) ||
    DEFAULT_CONDITION_OPERATION;
  const rawValue = get(condition, "rightOperand.value");
  const shownOperation =
    operation === CONDITION_OP_VALUES.IS_EMPTY && rawValue === false
      ? CONDITION_OP_VALUES.NOT_EMPTY
      : operation;
  const meta = operationByValue(shownOperation) || {};
  const operationLabel =
    get(labels, ["OPERATIONS", meta.labelKey]) || shownOperation;
  const field = isEmpty(accessor) ? null : get(variable, "label") || accessor;
  if (
    [CONDITION_OP_VALUES.IS_EMPTY, CONDITION_OP_VALUES.NOT_EMPTY].includes(
      shownOperation,
    )
  )
    return { field, operation: operationLabel, value: null };
  const otherAccessor = fieldOperandOf(condition);
  if (!isNil(otherAccessor)) {
    const other = keyBy(variables, "accessor")[otherAccessor];
    return {
      field,
      operation: operationLabel,
      value: isEmpty(otherAccessor)
        ? "?"
        : get(other, "label") || otherAccessor,
      fromField: true,
    };
  }
  const option = (get(variable, "options") || []).find(
    (item) => item.value === rawValue,
  );
  if (option) return { field, operation: operationLabel, value: option.label };
  if (typeof rawValue === "boolean")
    return {
      field,
      operation: operationLabel,
      value: rawValue ? labels.YES : labels.NO,
    };
  return {
    field,
    operation: operationLabel,
    value: isNil(rawValue) || rawValue === "" ? "?" : String(rawValue),
  };
};

const renumberRules = (edges, source, orderedIds) => {
  const orderById = orderedIds.reduce((orders, id, index) => {
    orders[id] = index + 1;
    return orders;
  }, {});
  return edges.map((edge) =>
    edge.source === source && !isNil(orderById[edge.id])
      ? { ...edge, data: { ...edge.data, order: orderById[edge.id] } }
      : edge,
  );
};

export const moveRuleTo = (edges = [], source, edgeId, to) => {
  const ids = rulesOf(edges, source).map((edge) => edge.id);
  const from = ids.indexOf(edgeId);
  if (from < 0 || to < 0 || to >= ids.length || from === to) return edges;
  const next = [...ids];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return renumberRules(edges, source, next);
};

export const moveRule = (edges = [], source, edgeId, delta) => {
  const from = rulesOf(edges, source).findIndex((edge) => edge.id === edgeId);
  return from < 0 ? edges : moveRuleTo(edges, source, edgeId, from + delta);
};

const newEdge = ({ id, source, target, data }) =>
  withLabel({
    id,
    source,
    target,
    label: "",
    data: {
      possibleStatusId: cuid(),
      order: 1,
      kind: EDGE_KINDS.CONDITION,
      event: "",
      delaySeconds: null,
      conditions: [],
      ...data,
    },
  });

export const addRule = (edges = [], { source, target, id = cuid() }) => {
  const rules = describeRules(edges, source);
  const ids = rules.map((rule) => rule.edge.id);
  const last = rules[rules.length - 1];
  const insertAt =
    last && last.mode === RULE_MODES.OTHERWISE ? ids.length - 1 : ids.length;
  ids.splice(insertAt, 0, id);
  return renumberRules(
    [...edges, newEdge({ id, source, target, data: {} })],
    source,
    ids,
  );
};

export const addExit = (edges = [], { source, target, type, id = cuid() }) => [
  ...edges,
  newEdge({
    id,
    source,
    target,
    data:
      type === EXIT_TYPES.TIMER
        ? { kind: EDGE_KINDS.TIMER, delaySeconds: DEFAULT_TIMER_SECONDS }
        : { kind: EDGE_KINDS.EVENT, event: "" },
  }),
];

export const updateEdge = (edges = [], edgeId, patch) =>
  edges.map((edge) => {
    if (edge.id !== edgeId) return edge;
    const { target, ...dataPatch } = patch;
    return withLabel({
      ...edge,
      target: target || edge.target,
      data: { ...edge.data, ...dataPatch },
    });
  });

export const updateRuleConditions = (edges = [], edgeId, conditions) =>
  updateEdge(edges, edgeId, { conditions, kind: EDGE_KINDS.CONDITION });

export const removeEdge = (edges = [], edgeId) => {
  const removed = edges.find((edge) => edge.id === edgeId);
  const rest = edges.filter((edge) => edge.id !== edgeId);
  if (!removed || !isRoutingEdge(removed)) return rest;
  return renumberRules(
    rest,
    removed.source,
    rulesOf(rest, removed.source).map((edge) => edge.id),
  );
};

export const splitDelay = (seconds) => {
  const total = Number(seconds) || 0;
  const unit =
    [DELAY_UNITS.DAYS, DELAY_UNITS.HOURS, DELAY_UNITS.MINUTES].find(
      (candidate) => total > 0 && total % SECONDS_BY_UNIT[candidate] === 0,
    ) || DELAY_UNITS.SECONDS;
  return { value: total / SECONDS_BY_UNIT[unit], unit };
};

export const toSeconds = (value, unit) =>
  Math.round((Number(value) || 0) * SECONDS_BY_UNIT[unit]);

export const borrowsPublishedScreen = (node, nodes = []) => {
  const lender = nodes.find(
    (item) => item.id === get(node, "data.config.screenFrom"),
  );
  return Boolean(get(lender, "data.screenPublished"));
};

export const isInputStep = (node, nodes = []) => {
  const data = get(node, "data") || {};
  if (!INPUT_NODE_TYPES.includes(data.nodeType || NODE_TYPES.STATUS))
    return false;
  if (!isEmpty(get(data, "formDefinition.fields"))) return true;
  if (data.screenPublished) return true;
  return borrowsPublishedScreen(node, nodes);
};

export const delayBlocker = (node, nodes = [], edges = []) => {
  if (isInputStep(node, nodes)) return { reason: DELAY_BLOCKERS.INPUT };
  if (isWaitingType(get(node, "data.nodeType")))
    return { reason: DELAY_BLOCKERS.WAIT };
  const catchAll = describeRules(edges, get(node, "id")).find(
    (rule) => rule.mode !== RULE_MODES.CONDITIONAL,
  );
  if (catchAll)
    return { reason: DELAY_BLOCKERS.CATCH_ALL, rank: catchAll.rank };
  return null;
};
